import { JavaScriptVariableType } from '@unifyapps/defs/types/javascriptVariable';
import _isNaN from 'lodash/isNaN';
import _isNil from 'lodash/isNil';
import isPlainObject from 'lodash/isPlainObject';
import _isString from 'lodash/isString';
import _toString from 'lodash/toString';

/**
 * Sanitizes a variable value according to its type, following the validation rules
 * from METHODS_DOCUMENTATION.md. Returns undefined for invalid values that don't match
 * the expected type.
 *
 * @param value - The value to sanitize
 * @param type - The expected variable type
 * @returns The sanitized value matching the type, or undefined if invalid
 */
export function sanitizeVariableValue(
  value: unknown,
  type: JavaScriptVariableType,
): string | number | boolean | unknown[] | Record<string, unknown> | undefined {
  switch (type) {
    case JavaScriptVariableType.String: {
      // if nil, or non-primitive (object/function/symbol) that can be coerced to string, return undefined
      if (_isNil(value)) {
        return undefined;
      }
      const valueType = typeof value;
      if (valueType === 'object' || valueType === 'function' || valueType === 'symbol') {
        return undefined;
      }
      return _toString(value);
    }
    case JavaScriptVariableType.Number: {
      // Number type: accept valid number or undefined
      // Invalid numbers (NaN) should return undefined
      if (_isNil(value)) {
        return undefined;
      }
      const numValue = Number(value);
      if (_isNaN(numValue)) {
        return undefined;
      }
      return numValue;
    }
    case JavaScriptVariableType.Boolean: {
      // Boolean type: accept any value or undefined
      // If undefined, preserve it (allows reset via SET operation)
      // String "true"/"false" are converted to boolean
      // Other values are converted to boolean
      if (_isNil(value)) {
        return undefined;
      }
      if (typeof value === 'string') {
        const lowerCaseValue = value.toLowerCase();
        if (lowerCaseValue === 'false') return false;
        if (lowerCaseValue === 'true') return true;
      }
      return Boolean(value);
    }
    case JavaScriptVariableType.Array: {
      // Array type: accept array or undefined
      return Array.isArray(value) ? value : undefined;
    }
    case JavaScriptVariableType.Object: {
      // Object type: accept plain object (not null, not array, not Date, etc.) or undefined
      if (_isString(value)) {
        // try to parse JSON string
        try {
          const parsed = JSON.parse(value);
          // ensure parsed result is a plain object
          return isPlainObject(parsed) ? parsed : undefined;
        } catch (error) {
          // console.error('Error while parsing variable value', error);
          return undefined;
        }
      }
      // check if it's a plain object (not Date, Array, RegExp, etc.)
      return isPlainObject(value) ? (value as Record<string, unknown>) : undefined;
    }
    default:
      return undefined;
  }
}
